<template>
  <div class="pa-4">
    <div class="d-flex justify-space-between align-center mb-4">
      <h2>Tenants</h2>
      <v-btn
        color="primary"
        prepend-icon="mdi-refresh"
        :loading="tenantsStore.loading"
        @click="refreshData"
      >
        Verversen
      </v-btn>
    </div>

    <!-- Loading State -->
    <v-progress-linear
      v-if="tenantsStore.loading"
      indeterminate
    ></v-progress-linear>

    <!-- Error State -->
    <v-alert v-if="tenantsStore.error" type="error" class="mb-4">
      {{ tenantsStore.error }}
    </v-alert>

    <!-- Empty State -->
    <v-card
      v-if="!tenantsStore.loading && tenantsStore.tenants.length === 0"
      class="pa-8 text-center"
    >
      <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-domain</v-icon>
      <h3 class="text-h6 mb-2">Geen tenants gevonden</h3>
      <p class="text-body-2 text-medium-emphasis mb-4">
        Er zijn nog geen tenants geconfigureerd.
      </p>
      <v-btn color="primary" prepend-icon="mdi-refresh" @click="refreshData">
        Opnieuw laden
      </v-btn>
    </v-card>

    <!-- Tenants List -->
    <v-card v-if="!tenantsStore.loading && tenantsStore.tenants.length > 0">
      <v-list>
        <v-list-item
          v-for="tenant in tenantsStore.tenants"
          :key="tenant.id"
          class="border-b"
          style="cursor: pointer"
          @click="goToDetail(tenant)"
        >
          <template #prepend>
            <div
              class="mr-3"
              style="cursor: pointer"
              @click.stop="openImageUpload(tenant)"
            >
              <v-avatar size="40">
                <v-img
                  v-if="tenant.app_icon_url"
                  :src="tenant.app_icon_url"
                  :alt="`${tenant.name} logo`"
                />
                <v-icon v-else color="grey">mdi-domain</v-icon>
              </v-avatar>
            </div>
          </template>

          <v-list-item-title class="d-flex align-center ga-2">
            <span>{{ tenant.display_name || tenant.name }}</span>
            <v-chip
              v-if="tenant.primary_color"
              size="small"
              :style="{ backgroundColor: tenant.primary_color, color: 'white' }"
            >
              {{ tenant.primary_color }}
            </v-chip>
          </v-list-item-title>
          <v-list-item-subtitle>
            <span class="text-medium-emphasis">Slug:</span> {{ tenant.slug }}
            <span class="mx-2">•</span>
            <span class="text-medium-emphasis">Subdomain:</span>
            {{ tenant.subdomain || "-" }}
          </v-list-item-subtitle>

          <template #append>
            <div class="d-flex ga-2 align-center">
              <v-chip size="small" color="blue" variant="outlined">
                <v-icon start size="16">mdi-account-multiple</v-icon>
                {{ tenant.users_count || 0 }}
              </v-chip>
              <v-chip size="small" color="green" variant="outlined">
                <v-icon start size="16">mdi-office-building</v-icon>
                {{ tenant.accounts_count || 0 }}
              </v-chip>
              <v-btn
                icon="mdi-pencil"
                size="small"
                variant="text"
                color="primary"
                @click.stop="editTenant(tenant)"
              ></v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Image Upload Dialog -->
    <v-dialog v-model="imageDialog" max-width="520px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-camera</v-icon>
          Tenant logo aanpassen
          <v-spacer></v-spacer>
          <v-btn icon @click="imageDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text>
          <ImageUpload
            v-if="imageTenant"
            :item="{
              id: imageAttachmentId,
              file: imageTenant.app_icon_url,
              can_change_image: true,
            }"
            record-type="Tenant"
            :record-id="imageTenant.id"
            field="app_icon"
            :on-upload-complete="onImageUploaded"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edit Tenant Dialog -->
    <TenantEditDialog
      v-model="editDialog"
      :tenant="selectedTenant"
      @saved="onTenantSaved"
    />
  </div>
</template>

<script>
import { useTenantsStoreRefactor } from "@/stores/tenantsStoreRefactor";
import TenantEditDialog from "./TenantEditDialog.vue";
import ImageUpload from "@/components/ui/ImageUpload.vue";

export default {
  name: "TenantsSettings",
  components: {
    TenantEditDialog,
    ImageUpload,
  },
  setup() {
    const tenantsStore = useTenantsStoreRefactor();
    return { tenantsStore };
  },
  data() {
    return {
      editDialog: false,
      selectedTenant: null,
      imageDialog: false,
      imageTenant: null,
      imageAttachmentId: null,
    };
  },
  async mounted() {
    // Wait for auth to ensure Authorization header is set before fetching
    try {
      const { useAuthStoreRefactor } = await import(
        "@/stores/authStoreRefactor"
      );
      const auth = useAuthStoreRefactor();
      if (!auth.token) {
        // Let router guard redirect if not logged in
        return;
      }
    } catch (e) {
      // Fallback: proceed, apiClient will 401 if unauthorized
    }
    await this.loadData();
  },
  methods: {
    async loadData() {
      try {
        await this.tenantsStore.fetchAll();
      } catch (error) {
        console.error("Error loading tenants:", error);
      }
    },
    async refreshData() {
      await this.loadData();
    },
    editTenant(tenant) {
      this.selectedTenant = { ...tenant };
      this.editDialog = true;
    },
    goToDetail(tenant) {
      this.$router.push(`/settings/tenants/${tenant.id}`);
    },
    openImageUpload(tenant) {
      this.imageTenant = tenant;
      this.imageAttachmentId =
        tenant.app_icon_attachment_id || tenant.app_icon_id || null;
      this.imageDialog = true;
    },
    async onImageUploaded() {
      this.imageDialog = false;
      // Refresh list or just the one tenant
      if (this.imageTenant?.id) {
        await this.tenantsStore.fetchById(this.imageTenant.id);
      } else {
        await this.tenantsStore.fetchAll();
      }
      this.imageTenant = null;
      this.imageAttachmentId = null;
    },
    async onTenantSaved(updatedTenant) {
      this.editDialog = false;
      this.selectedTenant = null;
      await this.tenantsStore.fetchById(updatedTenant.id);
    },
  },
};
</script>

<style scoped>
.border-b {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
</style>
